<template>
  <teleport to="body">
    <div v-if="show" class="modal-overlay location-search-overlay" @click.self="close">
      <div class="modal-container location-search-modal">
        <div class="modal-header">
          <button
            type="button"
            class="modal-close-button"
            @click="close"
            aria-label="Close search results"
          >
            &times;
          </button>
          <h3>Location search results</h3>
        </div>

        <div class="modal-body location-search-modal-body">
          <p class="instructions">
            Select the correct location from the list below:
          </p>

          <div class="search-results-list">
            <div
              v-for="result in results"
              :key="result.id"
              class="search-result-item"
              @click="select(result)"
            >
              <div class="result-info">
                <span class="location-name">{{ result.name }}</span>
                <span class="location-type">{{ result.type }}</span>
              </div>
              <span class="select-arrow">→</span>
            </div>
          </div>
        </div>

        <div class="modal-footer location-search-modal-footer">
          <button @click="close" class="search-again-btn">
            Search Again
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { defineProps, defineEmits } from 'vue'

export interface UnifiedSearchResult {
  id: string | number
  name: string
  type: string
  raw: any
}

const props = defineProps<{
  show: boolean
  results: UnifiedSearchResult[]
}>()

const emit = defineEmits(['close', 'select'])

const close = () => {
  emit('close')
}

const select = (result: UnifiedSearchResult) => {
  emit('select', result)
}
</script>

<style scoped>
.location-search-overlay {
  z-index: calc(var(--base-layout-modal-z-index) + 40);
}

.location-search-modal {
  width: min(640px, 100%);
  max-height: min(82vh, 700px);
}

.location-search-modal-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;
  overflow: hidden;
}

.location-search-modal-footer {
  position: relative;
  z-index: 2;
}

/* Scoped styles kept only for component-specific elements */
.instructions {
  color: var(--ui-inverse-text-muted);
  font-size: 0.95rem;
  margin-bottom: 0;
  font-family: var(--ui-inverse-font-family);
}

.search-results-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.15rem;
}

.search-result-item {
  background: rgba(16, 26, 52, 0.08);
  border: 1px solid var(--ui-inverse-border);
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  transition: all 0.2s ease;
}

.search-result-item:hover {
  background: rgba(16, 26, 52, 0.14);
  border-color: var(--ui-inverse-border-strong);
  transform: none;
}

.result-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.location-name {
  color: var(--ui-inverse-text);
  font-weight: 600;
  font-size: 1rem;
  font-family: var(--ui-inverse-font-family);
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  overflow: hidden;
}

.location-type {
  color: var(--ui-inverse-text-muted);
  font-size: 0.8rem;
  text-transform: capitalize;
  font-family: var(--ui-inverse-font-family);
}

.select-arrow {
  color: var(--ui-inverse-text);
  font-size: 1.25rem;
  font-weight: bold;
  padding-top: 0.15rem;
  margin-left: 0.75rem;
  flex-shrink: 0;
}

.search-again-btn {
  width: 100%;
  padding: 0.875rem;
  background: transparent;
  border: 1px solid var(--ui-inverse-border-strong);
  color: var(--ui-inverse-text);
  border-radius: 999px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: var(--ui-inverse-font-family);
}

.search-again-btn:hover {
  background: rgba(16, 26, 52, 0.1);
  border-color: rgba(16, 26, 52, 0.38);
}

@media (max-width: 768px) {
  .location-search-overlay {
    padding: var(--mobile-modal-overlay-top-padding) var(--mobile-modal-overlay-side-padding) var(--mobile-modal-overlay-bottom-padding);
  }

  .location-search-modal {
    max-height: min(var(--mobile-modal-max-height), 680px);
  }

  .location-search-modal-body {
    padding: 1rem;
    gap: 0.6rem;
  }

  .search-results-list {
    gap: 0.55rem;
  }

  .search-result-item {
    padding: 0.8rem;
  }

  .location-name {
    font-size: 0.92rem;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
}
</style>
